import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import PersonIcon from '@mui/icons-material/Person'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom'
import PhoneIcon from '@mui/icons-material/Phone'
import EmailIcon from '@mui/icons-material/Email'
import BadgeIcon from '@mui/icons-material/Badge'
import ContactSupportIcon from '@mui/icons-material/ContactSupport'
import StarIcon from '@mui/icons-material/Star'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import EditIcon from '@mui/icons-material/Edit'
import LanguageIcon from '@mui/icons-material/Language'
import NotesIcon from '@mui/icons-material/Notes'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import HistoryIcon from '@mui/icons-material/History'
import EventIcon from '@mui/icons-material/Event'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import PersonPinIcon from '@mui/icons-material/PersonPin'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { 
  CircularProgress, 
  Drawer, 
  Box, 
  Typography, 
  IconButton, 
  TextField, 
  Tabs,
  Tab,
  Button,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import { useState, useRef, useEffect } from 'react'
import { useScreenSize } from 'src/customHooks/customHooks'
import { useBodyScrollLock } from 'src/hooks/useBodyScrollLock'
import { loadOptionsForExistingPatient } from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingComponents'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import { formatPhoneNumber, validateEmail } from 'src/consumer/helpers/utilFunctions'
import { getAgeFromDobWithTime } from 'src/consumer/helpers/profilePageHelper'
import { GENDER_LIST_ONBOARDING, RELATIONSHIP_OPTIONS, displayNamesOfEventTypes } from 'src/consumer/constants'
import { getCPDataWithoutParams, postCPDataWithoutParams } from 'src/clinic/services'
import { isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'
import { getDOBWithTime } from 'src/consumer/helpers/profilePageHelper'
import { removePlusFromBeginning } from 'src/consumer/helpers/utilFunctions'
import {
  buildPatientUpsertPayload,
  normalizePatientInfoForForm,
} from 'src/clinic/components/ClinicManageBookingsPage/patientForm/utils/patientFormDataUtils'
import { addOrUpdatePatientsInIdb } from 'src/emr/database/indexedDbDao'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import NewPatientForm from './newPatient/NewPatientForm'
import LSPastVisitReportsComponent from 'src/consumer/components/LiveSessionPage/components/LSPastVisitReportsComponent/LSPastVisitReportsComponent'
import PatientFormComponentV2 from 'src/clinic/components/ClinicManageBookingsPage/patientForm/PatientFormComponentV2'
import PatientTagsComponent from 'src/clinic/components/ClinicManageBookingsPage/patientTags/PatientTagsComponent'

dayjs.extend(utc)

export default function PatientSearchDrawer({
  open,
  onClose,
  onSelectPatient,
  onBookAppointment,
  clinicId,
  clinicBrandId,
  mentorProfileData,
  searchPatientApiSignature = '/api/secure/mentor/patient/list',
  createPatientApiSignature = null,
  canViewPatientHistory = false,
  patientHistoryGetApiSignature = null,
  patientTagsApiSignature = null,
}) {
  const [activeTab, setActiveTab] = useState(0) // 0 = Search, 1 = Add New
  const [historyTab, setHistoryTab] = useState(0) // 0 = Visit History, 1 = Treatment History
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [isSearching, setIsSearching] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [selectedPatient, setSelectedPatient] = useState(null)
  const [showAddRelativeForm, setShowAddRelativeForm] = useState(false)
  const [viewingPatientDetails, setViewingPatientDetails] = useState(null)
  
  // Debug: Log permission values when they change
  useEffect(() => {
    if (viewingPatientDetails) {
      console.log('PatientSearchDrawer - Permission check:', {
        canViewPatientHistory,
        patientHistoryGetApiSignature,
        hasPermission: canViewPatientHistory && patientHistoryGetApiSignature,
      })
    }
  }, [viewingPatientDetails, canViewPatientHistory, patientHistoryGetApiSignature])
  const [prefillSearchQuery, setPrefillSearchQuery] = useState(null)
  const [patientTags, setPatientTags] = useState([]) // Used by handleTagsUpdated callback
  const [visitHistory, setVisitHistory] = useState([])
  const [visitHistoryLoading, setVisitHistoryLoading] = useState(false)
  const [editPatientDialogOpen, setEditPatientDialogOpen] = useState(false)
  const [editingPatientData, setEditingPatientData] = useState(null)
  const [isSavingPatient, setIsSavingPatient] = useState(false)
  const searchTimeoutRef = useRef(null)
  const drawerRef = useRef(null)
  const screenSize = useScreenSize()
  const shouldUsePatientSearchIdb = !searchPatientApiSignature?.includes('/api/secure/mentor/patient/list')

  // Use the standard scroll lock hook (same as other drawers)
  useBodyScrollLock(open, 1400)

  // Fix aria-hidden accessibility issue
  // Use MutationObserver to watch for and prevent aria-hidden on modal root when drawer has focusable content
  useEffect(() => {
    if (!open) return

    let observer = null
    let intervalId = null
    let handleFocus = null

    const fixAriaHidden = () => {
      if (!drawerRef.current) return

      const modalRoot = drawerRef.current.closest('.MuiModal-root')
      if (!modalRoot) return

      const drawerPaper = modalRoot.querySelector('.MuiDrawer-paper')
      if (!drawerPaper) return

      // Check if drawer has focusable content
      const hasFocusableContent = drawerPaper.querySelector(
        'input:not([disabled]), button:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), a[href], select:not([disabled])'
      )

      // Also check if drawer paper or any of its children currently has focus
      const activeElement = document.activeElement
      const hasFocus = drawerPaper.contains(activeElement)

      if (hasFocusableContent || hasFocus) {
        // Remove aria-hidden from modal root and backdrop
        modalRoot.removeAttribute('aria-hidden')
        
        const backdrop = modalRoot.querySelector('.MuiBackdrop-root')
        if (backdrop) {
          backdrop.removeAttribute('aria-hidden')
        }

        // Also check the presentation element
        const presentation = modalRoot.querySelector('[role="presentation"]')
        if (presentation) {
          presentation.removeAttribute('aria-hidden')
        }
      }
    }

    // Listen for focus events within the drawer
    handleFocus = () => {
      setTimeout(fixAriaHidden, 0)
    }

    // Initial fix after a short delay to let MUI render
    const initialTimer = setTimeout(() => {
      fixAriaHidden()

      // Set up MutationObserver to watch for aria-hidden changes
      const modalRoot = drawerRef.current?.closest('.MuiModal-root')
      if (modalRoot) {
        observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (
              mutation.type === 'attributes' &&
              mutation.attributeName === 'aria-hidden' &&
              modalRoot.contains(mutation.target)
            ) {
              // MUI set aria-hidden on modal root or its children, remove it if drawer has focusable content
              fixAriaHidden()
            }
          })
        })

        observer.observe(modalRoot, {
          attributes: true,
          attributeFilter: ['aria-hidden'],
          subtree: true, // Watch children too (backdrop, presentation)
        })
      }

      // Also set up an interval as a fallback to check periodically
      intervalId = setInterval(fixAriaHidden, 200)
      
      document.addEventListener('focusin', handleFocus, true)
    }, 100)

    return () => {
      clearTimeout(initialTimer)
      if (observer) {
        observer.disconnect()
      }
      if (intervalId) {
        clearInterval(intervalId)
      }
      if (handleFocus) {
        document.removeEventListener('focusin', handleFocus, true)
      }
    }
  }, [open])

  // Notify floating header about drawer state
  useEffect(() => {
    if (open) {
      // Tell floating header to ignore scroll events
      window.dispatchEvent(new CustomEvent('drawer-opened', { detail: { open: true } }))
    } else {
      // Tell floating header it can react to scroll events again
      window.dispatchEvent(new CustomEvent('drawer-opened', { detail: { open: false } }))
    }
  }, [open])

  // Load initial results when drawer opens on search tab
  useEffect(() => {
    if (open && activeTab === 0 && !hasSearched) {
      // Trigger a blank search to load initial results after a short delay
      const timer = setTimeout(() => {
        handleSearchQueryChange('')
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [open, activeTab, hasSearched])
  
  // Load visit history when viewing patient details
  useEffect(() => {
    if (viewingPatientDetails?.id) {
      // Load visit history
      loadVisitHistory(viewingPatientDetails.id)
      // Reset history tab to Visit History when patient changes
      setHistoryTab(0)
    } else {
      setVisitHistory([])
    }
  }, [viewingPatientDetails?.id])
  
  // Handle tags updated callback
  const handleTagsUpdated = (updatedTags) => {
    // Update local state if needed
    setPatientTags(updatedTags)
    // Optionally update viewingPatientDetails with new tags
    if (viewingPatientDetails) {
      setViewingPatientDetails({
        ...viewingPatientDetails,
        tags: updatedTags,
      })
    }
  }
  
  // Load visit history for patient
  const loadVisitHistory = async (patientId) => {
    if (!patientId || !clinicId) return
    
    setVisitHistoryLoading(true)
    try {
      // Use clinic assistant API if in clinic assistant context, otherwise use mentor API
      const apiEndpoint = searchPatientApiSignature === '/api/secure/ca/patient/list'
        ? '/api/secure/clinic-assistant/booked-event-history-of-a-patient'
        : '/api/secure/teacher/booked-event-history-of-a-patient'
      
      const response = await getCPDataWithoutParams(
        `${apiEndpoint}?patientId=${patientId}&clinicId=${clinicId}&sortDescendingOnStartTime=true`
      )
      
      if (
        response?.status === 200 &&
        isCommonResponseSuccessful(response?.data?.code) &&
        Array.isArray(response?.data?.data?.['booked-event-info'])
      ) {
        // Filter only booked events and sort by start time (newest first)
        const bookedEvents = response?.data?.data?.['booked-event-info']
          ?.filter((item) => item?.status === 'booked')
          ?.sort((a, b) => {
            const timeA = dayjs.utc(a?.startTime).valueOf()
            const timeB = dayjs.utc(b?.startTime).valueOf()
            return timeB - timeA // Descending order (newest first)
          })
        setVisitHistory(bookedEvents || [])
      } else {
        setVisitHistory([])
      }
    } catch (error) {
      console.error('Error fetching visit history:', error)
      setVisitHistory([])
    } finally {
      setVisitHistoryLoading(false)
    }
  }
  
  // Get patient since date
  const getPatientSince = (patient) => {
    const date = patient?.registeredOn || patient?.createdOn
    if (!date) return null
    return dayjs(date).format('DD MMM YYYY')
  }
  
  // Get relationship label
  const getRelationshipLabel = (relationship) => {
    if (!relationship) return null
    const relationshipOption = RELATIONSHIP_OPTIONS?.find(item => item.value === relationship)
    return relationshipOption?.label || relationship
  }
  
  // Get event type color
  const getEventTypeColor = (eventType) => {
    const colors = {
      consultation: '#6779e3',
      'therapy-session': '#7450aa',
      therapySession: '#7450aa',
      therapy: '#7450aa',
      screening: '#f0a6aa',
      assessment: '#875e8a',
      interview: '#f6c0aa',
    }
    return colors[eventType] || '#6779e3'
  }
  
  // Get card background color based on event type
  const getCardBackgroundColor = (eventType) => {
    if (eventType === 'consultation' || eventType === 'Consultation') {
      return 'rgba(103, 121, 227, 0.04)' // Light blue background for consultation
    }
    if (eventType === 'therapy-session' || eventType === 'therapySession' || eventType === 'therapy' || eventType === 'Therapy Session') {
      return 'rgba(116, 80, 170, 0.04)' // Light purple background for therapy
    }
    return 'white' // Default white for other types
  }

  // Handle search query changes with debounce
  const handleSearchQueryChange = async (query) => {
    setSearchQuery(query)
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    
    // Debounce the search (even for blank queries to show initial results)
    searchTimeoutRef.current = setTimeout(async () => {
      setIsSearching(true)
      
      try {
        let idbResults = []
        
        // First try IndexDB if authorized (wrap in try-catch in case IndexDB doesn't exist or fails)
        if (getAllRolesInBoolean().isDoctor && shouldUsePatientSearchIdb) {
          try {
            const { searchPatientsInIdb } = await import('src/emr/database/indexedDbDao')
            const patientsFromIdb = await searchPatientsInIdb(query)
            idbResults = (patientsFromIdb || [])
              ?.filter((item) => item?.clinicBrandId === clinicBrandId || item?.clinicId === clinicId)
              ?.map((option) => ({
                ...option,
                id: option?.id,
                name: option?.name || option?.firstName,
              }))
            
            // If we have IDB results, use them immediately
            if (idbResults && idbResults.length > 0) {
              setSearchResults(idbResults)
              setIsSearching(false)
              return
            }
          } catch (idbError) {
            // IndexDB not available or failed - continue to server API
            console.log('IndexDB not available, falling back to server API:', idbError)
          }
        }
        
        // If no IDB results or IndexDB failed, fetch from server
        // Add pageSize parameter to limit results to 20
        let apiSignature = searchPatientApiSignature
        if (!apiSignature.includes('pageSize')) {
          const separator = apiSignature.includes('?') ? '&' : '?'
          apiSignature = `${apiSignature}${separator}pageSize=20`
        }
        
        const fetchedData = await loadOptionsForExistingPatient(
          query,
          [],
          {
            page: 1,
            clinicId,
            clinicBrandId,
            mentorAuthorisedToSaveAndGetPatientsUsingIdb: getAllRolesInBoolean().isDoctor && shouldUsePatientSearchIdb,
            patientSearchApiSignature: apiSignature,
            setAutoFillStuffForNewPatientString: () => {},
          }
        )
        
        const results = fetchedData?.options || []
        
        // If search is a full contact (mobile/email) and we have results, 
        // add "Add Relative" option at the beginning
        let finalResults = results
        if (isFullContactSearch(query) && results.length > 0 && results.every(canViewPatientContactDetails)) {
          finalResults = [
            {
              id: 'add-relative-option',
              isAddRelativeOption: true,
              searchQuery: query,
            },
            ...results
          ]
        }
        
        setSearchResults(finalResults)
      } catch (error) {
        console.error('Error searching patients:', error)
        setSearchResults([])
      } finally {
        setIsSearching(false)
        setHasSearched(true)
      }
    }, 300)
  }

  const handleClose = () => {
    setSearchQuery('')
    setSearchResults([])
    setIsSearching(false)
    setHasSearched(false)
    setSelectedPatient(null)
    setShowAddRelativeForm(false)
    setViewingPatientDetails(null)
    setPrefillSearchQuery(null)
    setPatientTags([])
    setActiveTab(0)
    onClose()
  }
  
  // Helper function to get contact info display
  const canViewPatientContactDetails = (patient) => {
    return (
      patient?.responseContexts?.patientContactDetailsVisible !== false &&
      patient?.patientContactDetailsVisible !== false
    )
  }

  const getContactInfo = (patient) => {
    if (!canViewPatientContactDetails(patient)) {
      return { type: 'none', value: null, formatted: null }
    }
    // If anonymous patient, don't show system-generated email
    if (patient.anonymousPatient || patient.isAnonymous) {
      return { type: 'none', value: null, formatted: null }
    }
    
    // Prefer email over mobile if available
    if (patient.email) {
      return { type: 'email', value: patient.email, formatted: patient.email }
    }
    
    if (patient.mobile) {
      const phoneDetails = formatPhoneNumber(patient.mobile)
      const formatted = phoneDetails?.formattedPhoneNumber 
        ? `(${phoneDetails.countryCode}) ${phoneDetails.formattedPhoneNumber}`
        : phoneDetails?.concatenatedString || patient.mobile
      return { type: 'mobile', value: patient.mobile, formatted }
    }
    
    return { type: 'none', value: null, formatted: null }
  }
  
  // Check if patient is a relative (has primaryPatientProfile)
  const isRelativePatient = (patient) => {
    return !!patient?.primaryPatientProfile?.patientId
  }
  
  // Check if patient is a primary patient (has dependents)
  const isPrimaryPatient = (patient) => {
    // A patient is primary if they don't have a primaryPatientProfile
    // and we can check if they have dependents (this might need API data)
    return !patient?.primaryPatientProfile?.patientId
  }
  
  // Check if search query is a full mobile number or valid email
  const isFullContactSearch = (query) => {
    if (!query) return false
    // Check if it's a valid email
    if (validateEmail(query)) return true
    // Check if it's a full mobile number (typically 10+ digits, may include country code)
    const digitsOnly = query.replace(/\D/g, '')
    return digitsOnly.length >= 10
  }
  
  // Get age from dateOfBirth
  const getPatientAge = (dateOfBirth) => {
    if (!dateOfBirth) return null
    return getAgeFromDobWithTime(dateOfBirth)
  }
  
  // Get gender label
  const getGenderLabel = (gender) => {
    if (!gender) return null
    const genderOption = GENDER_LIST_ONBOARDING?.find(item => item.value === gender)
    return genderOption?.label || gender
  }
  
  // Get gender-specific face icon (using AccountCircle - represents face with hair)
  const getGenderIcon = (gender) => {
    // AccountCircle icon represents a face with hair, which is what we need
    return AccountCircleIcon
  }
  
  // Get gender-specific icon styling for face icons
  const getGenderIconStyle = (gender) => {
    if (!gender) return {}
    const genderLower = gender.toLowerCase()
    // AccountCircle already shows a face, we'll style it with gender-appropriate colors
    // The icon itself represents a face with hair, which is what we want
    return {
      // Make it slightly larger to show more detail
      transform: 'scale(1.15)',
    }
  }
  
  // Get avatar background color based on gender
  const getAvatarColor = (gender) => {
    if (!gender) return 'rgba(103, 121, 227, 0.1)'
    const genderLower = gender.toLowerCase()
    if (genderLower === 'male') return 'rgba(59, 130, 246, 0.1)' // Blue
    if (genderLower === 'female') return 'rgba(236, 72, 153, 0.1)' // Pink
    return 'rgba(103, 121, 227, 0.1)' // Default purple
  }
  
  // Get icon color based on gender
  const getIconColor = (gender) => {
    if (!gender) return '#6779e3'
    const genderLower = gender.toLowerCase()
    if (genderLower === 'male') return '#3b82f6' // Blue
    if (genderLower === 'female') return '#ec4899' // Pink
    return '#6779e3' // Default purple
  }

  const handlePatientSelect = (patient) => {
    if (onSelectPatient) {
      onSelectPatient(patient)
    }
    handleClose()
  }

  // Handle saving patient edits
  const handleSavePatientEdit = async () => {
    if (!editingPatientData || !editingPatientData.id) {
      toast.error('Invalid patient data')
      return
    }

    setIsSavingPatient(true)
    try {
      const dataToSubmit = buildPatientUpsertPayload(editingPatientData, {
        patientId: editingPatientData.id,
        clinicId,
        clinicBrandId,
        mentorId: mentorProfileData?.id,
        includePatientNumber: true,
      })

      const response = await postCPDataWithoutParams('/api/secure/patient/update', dataToSubmit)
      
      if (response.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
        const updatedPatient = response?.data?.data?.patient
        
        // Update IndexedDB if doctor role
        if (getAllRolesInBoolean().isDoctor) {
          addOrUpdatePatientsInIdb([updatedPatient])
        }
        
        // Reload patient data to ensure all fields are properly formatted
        try {
          const patientResponse = await getCPDataWithoutParams(
            `${searchPatientApiSignature}?patientId=${editingPatientData.id}&clinicId=${clinicId}`
          )
          if (patientResponse.status === 200 && patientResponse?.data?.[0]) {
            const reloadedPatient = patientResponse.data[0]
            // Update viewing patient details with reloaded data
            setViewingPatientDetails(reloadedPatient)
            
            // Update search results if patient is in results
            setSearchResults(prevResults => 
              prevResults.map(patient => 
                patient.id === reloadedPatient.id ? reloadedPatient : patient
              )
            )
          } else {
            // Fallback to updated patient from response
            setViewingPatientDetails(updatedPatient)
            setSearchResults(prevResults => 
              prevResults.map(patient => 
                patient.id === updatedPatient.id ? updatedPatient : patient
              )
            )
          }
        } catch (reloadError) {
          console.error('Error reloading patient data:', reloadError)
          // Fallback to updated patient from response
          setViewingPatientDetails(updatedPatient)
          setSearchResults(prevResults => 
            prevResults.map(patient => 
              patient.id === updatedPatient.id ? updatedPatient : patient
            )
          )
        }
        
        toast.success('Patient details updated successfully')
        setEditPatientDialogOpen(false)
        setEditingPatientData(null)
      } else {
        toast.error('Failed to update patient details')
      }
    } catch (error) {
      console.error('Error updating patient:', error)
      toast.error('Error updating patient details')
    } finally {
      setIsSavingPatient(false)
    }
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
    setShowAddRelativeForm(false)
    setSelectedPatient(null)
    // Clear prefill when switching away from Add New tab
    if (newValue !== 1) {
      setPrefillSearchQuery(null)
    }
  }

  const handleNewPatientAdded = (patientInfo) => {
    if (onSelectPatient) {
      onSelectPatient(patientInfo)
    }
    handleClose()
  }

  // Render patient details content (reusable for desktop and mobile)
  const renderPatientDetailsContent = () => {
    if (!viewingPatientDetails) return null
    
    return (
      <>
        {/* Patient Header with Avatar, Age, Gender, Contact, Language, Tags */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'flex-start', 
          gap: 1.5, 
          mb: 2,
          padding: screenSize.width > 768 ? '12px 16px' : '16px',
          borderRadius: '10px',
          backgroundColor: 'rgba(103, 121, 227, 0.02)',
          border: '1px solid rgba(103, 121, 227, 0.1)',
          position: 'relative',
        }}>
          {/* Close button in top right corner (Desktop only) */}
          {screenSize.width > 768 && (
            <IconButton
              size="small"
              onClick={() => setViewingPatientDetails(null)}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: '#64748b',
                padding: '4px',
                '&:hover': {
                  backgroundColor: 'rgba(100, 116, 139, 0.1)',
                },
              }}
            >
              <CloseIcon sx={{ fontSize: '16px' }} />
            </IconButton>
          )}
          
          <Box
            sx={{
              width: screenSize.width > 768 ? 64 : 72,
              height: screenSize.width > 768 ? 64 : 72,
              borderRadius: '16px',
              backgroundColor: getAvatarColor(viewingPatientDetails.gender),
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              position: 'relative',
              border: `3px solid ${getIconColor(viewingPatientDetails.gender)}40`,
              overflow: 'visible',
              paddingBottom: getPatientAge(viewingPatientDetails.dateOfBirth) ? '8px' : '0',
            }}
          >
            {(() => {
              const GenderIcon = getGenderIcon(viewingPatientDetails.gender)
              const iconStyle = getGenderIconStyle(viewingPatientDetails.gender)
              return (
                <GenderIcon 
                  sx={{ 
                    fontSize: screenSize.width > 768 ? 32 : 36, 
                    color: getIconColor(viewingPatientDetails.gender),
                    ...iconStyle,
                  }} 
                />
              )
            })()}
            {getPatientAge(viewingPatientDetails.dateOfBirth) && (
              <Typography
                sx={{
                  position: 'absolute',
                  bottom: -4,
                  fontSize: screenSize.width > 768 ? '11px' : '12px',
                  fontWeight: 700,
                  color: 'white',
                  backgroundColor: getIconColor(viewingPatientDetails.gender),
                  borderRadius: '8px',
                  padding: screenSize.width > 768 ? '2px 6px' : '3px 8px',
                  lineHeight: 1.2,
                  boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
                  border: '2px solid white',
                  minWidth: screenSize.width > 768 ? '28px' : '32px',
                  textAlign: 'center',
                }}
              >
                {getPatientAge(viewingPatientDetails.dateOfBirth)}
              </Typography>
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, pr: screenSize.width > 768 ? 3 : 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', fontSize: screenSize.width > 768 ? '16px' : '18px', flex: 1 }}>
                {viewingPatientDetails.name}
              </Typography>
              {canViewPatientContactDetails(viewingPatientDetails) && (
                <IconButton
                  size="small"
                  onClick={() => {
                    setEditingPatientData(normalizePatientInfoForForm(viewingPatientDetails))
                    setEditPatientDialogOpen(true)
                  }}
                  sx={{
                    color: '#6779e3',
                    backgroundColor: 'rgba(103, 121, 227, 0.1)',
                    padding: screenSize.width > 768 ? '6px' : '8px',
                    '&:hover': {
                      backgroundColor: 'rgba(103, 121, 227, 0.2)',
                      transform: 'scale(1.05)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <EditIcon sx={{ fontSize: screenSize.width > 768 ? 18 : 20 }} />
                </IconButton>
              )}
            </Box>
            
            {/* Patient Number, Gender, Age - Compact inline */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 0.75 }}>
              {viewingPatientDetails.patientNumber && (
                <Typography variant="body2" sx={{ color: '#64748b', fontSize: screenSize.width > 768 ? '11px' : '12px', fontWeight: 600, fontFamily: 'monospace' }}>
                  #{viewingPatientDetails.patientNumber}
                </Typography>
              )}
              {viewingPatientDetails.gender && (
                <>
                  <Typography variant="body2" sx={{ color: '#94a3b8', fontSize: '10px' }}>
                    •
                  </Typography>
                  <Chip
                    label={getGenderLabel(viewingPatientDetails.gender)}
                    size="small"
                    sx={{
                      height: screenSize.width > 768 ? '18px' : '20px',
                      fontSize: screenSize.width > 768 ? '10px' : '11px',
                      fontWeight: 600,
                      backgroundColor: getAvatarColor(viewingPatientDetails.gender),
                      color: getIconColor(viewingPatientDetails.gender),
                      border: `1px solid ${getIconColor(viewingPatientDetails.gender)}30`,
                    }}
                  />
                </>
              )}
              {getPatientAge(viewingPatientDetails.dateOfBirth) && (
                <>
                  <Typography variant="body2" sx={{ color: '#94a3b8', fontSize: '10px' }}>
                    •
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', fontSize: screenSize.width > 768 ? '11px' : '12px', fontWeight: 600 }}>
                    {getPatientAge(viewingPatientDetails.dateOfBirth)}y
                  </Typography>
                </>
              )}
            </Box>
            
            {/* Contact Info - Compact */}
            {(() => {
              const contactInfo = getContactInfo(viewingPatientDetails)
              if (contactInfo.type === 'email') {
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <EmailIcon sx={{ fontSize: screenSize.width > 768 ? 14 : 16, color: '#6779e3' }} />
                    <Typography variant="body2" sx={{ color: '#1e293b', fontSize: screenSize.width > 768 ? '12px' : '13px', fontWeight: 500 }}>
                      {contactInfo.value}
                    </Typography>
                  </Box>
                )
              }
              if (contactInfo.type === 'mobile') {
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <PhoneIcon sx={{ fontSize: screenSize.width > 768 ? 14 : 16, color: '#6779e3' }} />
                    <Typography variant="body2" sx={{ color: '#1e293b', fontSize: screenSize.width > 768 ? '12px' : '13px', fontWeight: 500 }}>
                      {contactInfo.formatted}
                    </Typography>
                  </Box>
                )
              }
              if (contactInfo.type === 'none') {
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <ContactSupportIcon sx={{ fontSize: screenSize.width > 768 ? 14 : 16, color: '#f0a6aa' }} />
                    <Typography variant="body2" sx={{ color: '#94a3b8', fontSize: screenSize.width > 768 ? '11px' : '12px', fontStyle: 'italic' }}>
                      No contact details
                    </Typography>
                  </Box>
                )
              }
              return null
            })()}
            
            {/* Language - Compact */}
            {viewingPatientDetails?.defaultCommunicationLanguage && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <LanguageIcon sx={{ fontSize: screenSize.width > 768 ? 14 : 16, color: '#6779e3' }} />
                <Typography variant="body2" sx={{ color: '#1e293b', fontSize: screenSize.width > 768 ? '12px' : '13px', fontWeight: 500 }}>
                  {viewingPatientDetails.defaultCommunicationLanguage}
                </Typography>
              </Box>
            )}
            
            {/* Tags - Using PatientTagsComponent */}
            {patientTagsApiSignature && viewingPatientDetails?.id && (
              <PatientTagsComponent
                patientId={viewingPatientDetails.id}
                patientTagsApiSignature={patientTagsApiSignature}
                initialTags={viewingPatientDetails?.tags || []}
                compact={screenSize.width <= 768}
                onTagsUpdated={handleTagsUpdated}
                sx={{ mt: 0.75 }}
              />
            )}
          </Box>
        </Box>
        
        {/* Book Appointment Button - After Header, Before History */}
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => {
              if (onBookAppointment && viewingPatientDetails) {
                onBookAppointment(viewingPatientDetails)
              } else if (viewingPatientDetails) {
                // Fallback: if onBookAppointment is not provided, just select the patient
                handlePatientSelect(viewingPatientDetails)
              }
            }}
            disabled={!viewingPatientDetails}
            startIcon={<CalendarTodayIcon sx={{ fontSize: screenSize.width > 768 ? 18 : 20 }} />}
            sx={{
              backgroundColor: '#6779e3',
              color: 'white',
              fontWeight: 600,
              padding: screenSize.width > 768 ? '12px 24px' : '14px 24px',
              borderRadius: '12px',
              textTransform: 'none',
              fontSize: screenSize.width > 768 ? '14px' : '15px',
              boxShadow: '0 2px 8px rgba(103, 121, 227, 0.25)',
              '&:hover': {
                backgroundColor: '#7450aa',
                boxShadow: '0 4px 12px rgba(116, 80, 170, 0.35)',
                transform: 'translateY(-1px)',
              },
              '&.Mui-disabled': {
                backgroundColor: 'rgba(103, 121, 227, 0.3)',
                color: 'rgba(255, 255, 255, 0.5)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            Book Appointment
          </Button>
        </Box>
        
        <Divider sx={{ my: 1.5 }} />
        
        {/* History Section with Tabs */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <HistoryIcon sx={{ fontSize: screenSize.width > 768 ? 22 : 24, color: '#6779e3' }} />
            <Typography variant="h6" sx={{ color: '#1e293b', fontSize: screenSize.width > 768 ? '16px' : '18px', fontWeight: 700 }}>
              History
            </Typography>
          </Box>
          
          {/* Tabs for Visit History and Treatment History */}
          <Tabs
            value={historyTab}
            onChange={(e, newValue) => {
              // Only allow switching to Treatment History if permission is granted
              if (newValue === 1 && (!canViewPatientHistory || !patientHistoryGetApiSignature)) {
                return
              }
              setHistoryTab(newValue)
            }}
            sx={{
              mb: 2,
              borderBottom: '1px solid rgba(103, 121, 227, 0.2)',
              '& .MuiTabs-indicator': {
                backgroundColor: '#6779e3',
                height: 2,
              },
            }}
          >
            <Tab
              label="Visit History"
              sx={{
                textTransform: 'none',
                fontWeight: historyTab === 0 ? 600 : 500,
                fontSize: screenSize.width > 768 ? '14px' : '15px',
                color: historyTab === 0 ? '#6779e3' : '#64748b',
                minHeight: 40,
              }}
            />
            <Tab
              label="Treatment History"
              disabled={!canViewPatientHistory || !patientHistoryGetApiSignature}
              sx={{
                textTransform: 'none',
                fontWeight: historyTab === 1 ? 600 : 500,
                fontSize: screenSize.width > 768 ? '14px' : '15px',
                color: historyTab === 1 
                  ? '#6779e3' 
                  : (!canViewPatientHistory || !patientHistoryGetApiSignature 
                    ? 'rgba(0, 0, 0, 0.26)' 
                    : '#64748b'),
                minHeight: 40,
                '&.Mui-disabled': {
                  opacity: 0.5,
                },
              }}
            />
          </Tabs>
          
          {/* Visit History Content */}
          {(!canViewPatientHistory || !patientHistoryGetApiSignature || historyTab === 0) && (
            <Box>
          
          {visitHistoryLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
              <CircularProgress size={24} sx={{ color: '#6779e3' }} />
            </Box>
          ) : visitHistory.length > 0 ? (
            <Box
              sx={{
                position: 'relative',
                paddingLeft: screenSize.width > 768 ? '32px' : '28px',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: screenSize.width > 768 ? '15px' : '13px',
                  top: 0,
                  bottom: 0,
                  width: '2px',
                  backgroundColor: 'rgba(103, 121, 227, 0.2)',
                },
              }}
            >
              {visitHistory.map((visit, index) => {
                const eventTypeColor = getEventTypeColor(visit.eventType)
                const eventTypeName = displayNamesOfEventTypes[visit.eventType] || visit.eventType || 'Session'
                const visitDate = dayjs.utc(visit.startTime).local()
                const now = dayjs()
                const yesterday = dayjs().subtract(1, 'day')
                const startOfThisWeek = now.startOf('week')
                const startOfLastWeek = now.subtract(1, 'week').startOf('week')
                const thisMonth = now.startOf('month')
                const lastMonth = now.subtract(1, 'month').startOf('month')
                const thisYear = now.startOf('year')
                const lastYear = now.subtract(1, 'year').startOf('year')
                
                // Determine time period tags (check in order of specificity)
                const isToday = visitDate.isSame(now, 'day')
                const isYesterday = visitDate.isSame(yesterday, 'day')
                const isThisWeek = !isToday && !isYesterday && (visitDate.isAfter(startOfThisWeek) || visitDate.isSame(startOfThisWeek))
                const isLastWeek = (visitDate.isAfter(startOfLastWeek) || visitDate.isSame(startOfLastWeek)) && visitDate.isBefore(startOfThisWeek)
                const isThisMonth = !isThisWeek && !isLastWeek && (visitDate.isAfter(thisMonth) || visitDate.isSame(thisMonth)) && visitDate.isBefore(now.endOf('month').add(1, 'day'))
                const isLastMonth = (visitDate.isAfter(lastMonth) || visitDate.isSame(lastMonth)) && visitDate.isBefore(thisMonth)
                const isThisYear = !isThisMonth && !isLastMonth && (visitDate.isAfter(thisYear) || visitDate.isSame(thisYear))
                const isLastYear = (visitDate.isAfter(lastYear) || visitDate.isSame(lastYear)) && visitDate.isBefore(thisYear)
                
                // Get time period label
                let timePeriodLabel = null
                let timePeriodColor = null
                if (isToday) {
                  timePeriodLabel = 'Today'
                  timePeriodColor = '#f0a6aa'
                } else if (isYesterday) {
                  timePeriodLabel = 'Yesterday'
                  timePeriodColor = '#f0a6aa'
                } else if (isThisWeek) {
                  timePeriodLabel = 'This Week'
                  timePeriodColor = '#f6c0aa'
                } else if (isLastWeek) {
                  timePeriodLabel = 'Last Week'
                  timePeriodColor = '#f6c0aa'
                } else if (isThisMonth) {
                  timePeriodLabel = 'This Month'
                  timePeriodColor = '#875e8a'
                } else if (isLastMonth) {
                  timePeriodLabel = 'Last Month'
                  timePeriodColor = '#875e8a'
                } else if (isThisYear) {
                  timePeriodLabel = 'This Year'
                  timePeriodColor = '#7450aa'
                } else if (isLastYear) {
                  timePeriodLabel = 'Last Year'
                  timePeriodColor = '#7450aa'
                }
                
                // Calculate visit number for consultation, therapy-session, assessment (not screening)
                // Counts are separate for each event type
                // Older items have lower numbers (1, 2, 3...), newer have higher numbers
                const shouldNumber = ['consultation', 'therapy-session', 'therapySession', 'assessment'].includes(visit.eventType)
                let visitNumber = null
                let visitNumberText = null
                if (shouldNumber) {
                  // Filter visits of the same type only
                  const sameTypeVisits = visitHistory.filter(v => v.eventType === visit.eventType)
                  // Count how many visits of this same type come after this one (are older)
                  const olderCount = visitHistory.slice(index + 1).filter(v => v.eventType === visit.eventType).length
                  // Oldest visit (last in array) gets 1, newest (first) gets highest number
                  visitNumber = olderCount + 1
                  
                  // Convert to ordinal (1st, 2nd, 3rd, 4th, etc.)
                  const getOrdinal = (n) => {
                    const s = ['th', 'st', 'nd', 'rd']
                    const v = n % 100
                    return n + (s[(v - 20) % 10] || s[v] || s[0])
                  }
                  visitNumberText = getOrdinal(visitNumber)
                }
                
                return (
                  <Box
                    key={visit.id || index}
                    sx={{
                      position: 'relative',
                      mb: 3,
                      '&:last-child': {
                        mb: 0,
                      },
                    }}
                  >
                    {/* Timeline Dot */}
                    <Box
                      sx={{
                        position: 'absolute',
                        left: screenSize.width > 768 ? '-27px' : '-23px',
                        top: '4px',
                        width: screenSize.width > 768 ? '24px' : '20px',
                        height: screenSize.width > 768 ? '24px' : '20px',
                        borderRadius: '50%',
                        backgroundColor: eventTypeColor,
                        border: '3px solid white',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        zIndex: 1,
                      }}
                    />
                    
                    {/* Visit Card */}
                    <Box
                      sx={{
                        backgroundColor: getCardBackgroundColor(visit.eventType),
                        borderRadius: '12px',
                        padding: screenSize.width > 768 ? '18px' : '16px',
                        border: `2px solid ${eventTypeColor}`,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                          transform: 'translateX(4px)',
                          borderColor: eventTypeColor,
                          borderWidth: '2.5px',
                          backgroundColor: getCardBackgroundColor(visit.eventType) === 'white' 
                            ? 'rgba(248, 250, 252, 1)' 
                            : getCardBackgroundColor(visit.eventType).replace('0.04', '0.08'),
                        },
                      }}
                    >
                      {/* Event Type Badge with Number */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: screenSize.width > 768 ? 'nowrap' : 'wrap', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {visitNumberText ? (
                            <Chip
                              label={`${visitNumberText} ${eventTypeName}`}
                              size="small"
                              sx={{
                                backgroundColor: `${eventTypeColor}15`,
                                color: eventTypeColor,
                                fontWeight: 700,
                                fontSize: screenSize.width > 768 ? '12px' : '11px',
                                height: screenSize.width > 768 ? '26px' : '24px',
                                border: `1.5px solid ${eventTypeColor}`,
                              }}
                            />
                          ) : (
                            <Chip
                              label={eventTypeName}
                              size="small"
                              sx={{
                                backgroundColor: `${eventTypeColor}15`,
                                color: eventTypeColor,
                                fontWeight: 700,
                                fontSize: screenSize.width > 768 ? '12px' : '11px',
                                height: screenSize.width > 768 ? '26px' : '24px',
                                border: `1.5px solid ${eventTypeColor}`,
                              }}
                            />
                          )}
                        </Box>
                        {timePeriodLabel && (
                          <Chip
                            label={timePeriodLabel}
                            size="small"
                            sx={{
                              backgroundColor: `${timePeriodColor}15`,
                              color: timePeriodColor,
                              fontWeight: 600,
                              fontSize: screenSize.width > 768 ? '10px' : '9px',
                              height: screenSize.width > 768 ? '22px' : '20px',
                              border: `1px solid ${timePeriodColor}30`,
                            }}
                          />
                        )}
                      </Box>
                      
                      {/* Date and Time - Prominent */}
                      <Box sx={{ mb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                          <EventIcon sx={{ fontSize: screenSize.width > 768 ? 18 : 16, color: '#6779e3' }} />
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              color: '#1e293b', 
                              fontSize: screenSize.width > 768 ? '15px' : '14px', 
                              fontWeight: 700,
                              letterSpacing: '0.3px',
                            }}
                          >
                            {visitDate.format('dddd, DD MMM YYYY')}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: screenSize.width > 768 ? '26px' : '24px' }}>
                          <AccessTimeIcon sx={{ fontSize: screenSize.width > 768 ? 16 : 14, color: '#64748b' }} />
                          <Typography variant="body2" sx={{ color: '#64748b', fontSize: screenSize.width > 768 ? '13px' : '12px', fontWeight: 500 }}>
                            {visitDate.format('h:mm A')}
                          </Typography>
                        </Box>
                      </Box>
                      
                      {/* Mentor Name */}
                      {visit.mentorName && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                          <PersonPinIcon sx={{ fontSize: screenSize.width > 768 ? 16 : 14, color: '#6779e3' }} />
                          <Typography variant="body2" sx={{ color: '#64748b', fontSize: screenSize.width > 768 ? '12px' : '11px' }}>
                            with {visit.mentorName}
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Duration */}
                      {visit.durationInMins && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" sx={{ color: '#94a3b8', fontSize: screenSize.width > 768 ? '11px' : '10px' }}>
                            Duration: {visit.durationInMins} minutes
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )
              })}
            </Box>
          ) : (
            <Box sx={{ padding: '24px', borderRadius: '8px', backgroundColor: 'rgba(103, 121, 227, 0.02)', border: '1px dashed rgba(103, 121, 227, 0.2)', textAlign: 'center' }}>
              <HistoryIcon sx={{ fontSize: 32, color: '#cbd5e1', mb: 1 }} />
              <Typography variant="body2" sx={{ color: '#94a3b8', fontSize: '13px' }}>
                No visit history found
              </Typography>
              <Typography variant="body2" sx={{ color: '#cbd5e1', fontSize: '11px', mt: 0.5 }}>
                This patient hasn&apos;t had any sessions yet
              </Typography>
            </Box>
          )}
            </Box>
          )}
          
          {/* Treatment History Content */}
          {canViewPatientHistory && patientHistoryGetApiSignature && historyTab === 1 && (
            <Box>
              <LSPastVisitReportsComponent
                skeletonNumber={2}
                hidePrintButton={true}
                patientId={viewingPatientDetails?.id}
                typeOfReportsToShowArray={['prescription', 'therapy', 'assessment', 'screening']}
                hideSendToDoctorButton={true}
                patientHistoryGetApiSignature={patientHistoryGetApiSignature}
                hideVisitSummary={searchPatientApiSignature === '/api/secure/ca/patient/list'} // Hide summary blocks for Clinic Assistant
                hidePrivateNotes={searchPatientApiSignature === '/api/secure/ca/patient/list'} // Hide private notes fetching for Clinic Assistant
                customLoader={
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
                    <CircularProgress size={40} sx={{ color: 'rgba(67, 56, 202, 0.7)', mb: 2 }} />
                    <Typography variant="body2" sx={{ color: 'rgba(67, 56, 202, 0.6)' }}>
                      Loading treatment history...
                    </Typography>
                  </Box>
                }
              />
            </Box>
          )}
        </Box>
        
        {/* Other Details */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Patient Since */}
          {getPatientSince(viewingPatientDetails) && (
            <Box>
              <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1, display: 'block' }}>
                Patient Since
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, padding: '12px', borderRadius: '8px', backgroundColor: 'rgba(103, 121, 227, 0.02)' }}>
                <CalendarTodayIcon sx={{ fontSize: screenSize.width > 768 ? 18 : 20, color: '#6779e3' }} />
                <Typography variant="body2" sx={{ color: '#1e293b', fontSize: screenSize.width > 768 ? '14px' : '15px' }}>
                  {getPatientSince(viewingPatientDetails)}
                </Typography>
              </Box>
            </Box>
          )}
          
          {/* Related To & Relationship */}
          {viewingPatientDetails?.primaryPatientProfile?.patientId && viewingPatientDetails?.primaryPatientProfile?.dependentRelation && (
            <Box>
              <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1, display: 'block' }}>
                Relationship
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, padding: '12px', borderRadius: '8px', backgroundColor: 'rgba(116, 80, 170, 0.05)' }}>
                  <PersonOutlineIcon sx={{ fontSize: screenSize.width > 768 ? 18 : 20, color: '#7450aa' }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ color: '#1e293b', fontSize: screenSize.width > 768 ? '13px' : '14px', fontWeight: 500 }}>
                      Related to: {viewingPatientDetails.primaryPatientProfile?.name || 'Primary Patient'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', fontSize: screenSize.width > 768 ? '12px' : '13px', mt: 0.5 }}>
                      Relationship: {getRelationshipLabel(viewingPatientDetails.primaryPatientProfile.dependentRelation)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
          
          {/* Notes */}
          {viewingPatientDetails?.notes && (
            <Box>
              <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1, display: 'block' }}>
                Notes
              </Typography>
              <Box sx={{ padding: '12px', borderRadius: '8px', backgroundColor: 'rgba(103, 121, 227, 0.02)', border: '1px solid rgba(103, 121, 227, 0.1)' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <NotesIcon sx={{ fontSize: screenSize.width > 768 ? 18 : 20, color: '#6779e3', mt: 0.5 }} />
                  <Typography variant="body2" sx={{ color: '#1e293b', fontSize: screenSize.width > 768 ? '14px' : '15px', whiteSpace: 'pre-wrap' }}>
                    {viewingPatientDetails.notes}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
        
        <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid rgba(226, 232, 240, 0.8)' }}>
          {onBookAppointment && (
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                if (onBookAppointment && viewingPatientDetails) {
                  onBookAppointment(viewingPatientDetails)
                }
              }}
              sx={{
                borderColor: '#6779e3',
                color: '#6779e3',
                fontWeight: 600,
                padding: screenSize.width > 768 ? '12px 24px' : '14px 24px',
                borderRadius: '10px',
                textTransform: 'none',
                mb: 2,
                fontSize: screenSize.width > 768 ? '14px' : '15px',
                '&:hover': {
                  borderColor: '#7450aa',
                  color: '#7450aa',
                  backgroundColor: 'rgba(103, 121, 227, 0.08)',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              Book Appointment
            </Button>
          )}
          <Button
            variant="contained"
            fullWidth
            onClick={() => handlePatientSelect(viewingPatientDetails)}
            sx={{
              backgroundColor: '#6779e3',
              color: 'white',
              fontWeight: 600,
              padding: screenSize.width > 768 ? '12px 24px' : '14px 24px',
              borderRadius: '10px',
              textTransform: 'none',
              fontSize: screenSize.width > 768 ? '14px' : '15px',
              boxShadow: '0 2px 8px rgba(103, 121, 227, 0.25)',
              '&:hover': {
                backgroundColor: '#7450aa',
                boxShadow: '0 4px 12px rgba(116, 80, 170, 0.35)',
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            Select Patient
          </Button>
        </Box>
      </>
    )
  }

  const renderSearchTab = () => {
    const contactInfo = viewingPatientDetails ? getContactInfo(viewingPatientDetails) : null
    
    return (
      <Box sx={{ display: 'flex', height: '100%', minHeight: 0 }}>
        {/* Left Panel - Search Results */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          width: viewingPatientDetails && screenSize.width > 768 ? '50%' : '100%',
          borderRight: viewingPatientDetails && screenSize.width > 768 ? '1px solid rgba(226, 232, 240, 0.8)' : 'none',
          transition: 'width 0.3s ease',
        }}>
          {/* Search Box */}
          <Box sx={{ padding: '16px 20px', borderBottom: '1px solid rgba(226, 232, 240, 0.8)', flexShrink: 0 }}>
            <TextField
              fullWidth
              placeholder="Search by name, mobile, email, patient number..."
              value={searchQuery}
              onChange={(e) => handleSearchQueryChange(e.target.value)}
              autoFocus
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ color: '#64748b', marginRight: '8px', fontSize: 20 }} />
                ),
                endAdornment: searchQuery && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchQuery('')
                      setSearchResults([])
                      setHasSearched(false)
                    }}
                  >
                    <CloseIcon sx={{ fontSize: '18px' }} />
                  </IconButton>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  '& fieldset': {
                    borderColor: 'rgba(103, 121, 227, 0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(103, 121, 227, 0.4)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'rgba(103, 121, 227, 0.6)',
                  },
                },
              }}
            />
          </Box>

          {/* Search Results */}
          <Box sx={{ flex: 1, overflowY: 'auto', padding: '16px 20px', minHeight: 0 }}>
        {isSearching ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px 20px' }}>
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={40} sx={{ color: '#6779e3' }} />
              <Typography variant="body2" sx={{ mt: 2, color: '#64748b' }}>
                Searching patients...
              </Typography>
            </Box>
          </Box>
        ) : searchResults.length > 0 ? (
          <>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '13px' }}>
                Found {searchResults.length} patient{searchResults.length > 1 ? 's' : ''}
              </Typography>
              {hasSearched && searchQuery && (
                <Chip 
                  label={`"${searchQuery}"`} 
                  size="small"
                  sx={{ 
                    backgroundColor: 'rgba(103, 121, 227, 0.1)',
                    color: '#6779e3',
                    fontWeight: 500,
                  }}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {searchResults.map((patient) => {
                // Handle "Add Relative" option
                if (patient.isAddRelativeOption) {
                  return (
                    <Box
                      key={patient.id}
                      onClick={(e) => {
                        e.stopPropagation()
                        setPrefillSearchQuery(patient.searchQuery)
                        setActiveTab(1)
                      }}
                      sx={{
                        padding: '20px',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        border: '2px solid #7450aa',
                        backgroundColor: 'rgba(116, 80, 170, 0.08)',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 4px 12px rgba(116, 80, 170, 0.2)',
                        '&:hover': {
                          borderColor: '#875e8a',
                          backgroundColor: 'rgba(116, 80, 170, 0.12)',
                          boxShadow: '0 6px 16px rgba(116, 80, 170, 0.3)',
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: '14px',
                            backgroundColor: '#7450aa',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <PersonAddIcon sx={{ fontSize: 32, color: 'white' }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontSize: '18px',
                              fontWeight: 700,
                              color: '#7450aa',
                              marginBottom: '4px',
                            }}
                          >
                            Add Relative Patient
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '13px',
                              color: '#64748b',
                            }}
                          >
                            Add a new patient with {validateEmail(patient.searchQuery) ? 'email' : 'mobile'}: {patient.searchQuery}
                          </Typography>
                        </Box>
                        <FamilyRestroomIcon sx={{ fontSize: 28, color: '#7450aa' }} />
                      </Box>
                    </Box>
                  )
                }
                
                const contactInfo = getContactInfo(patient)
                const isSelected = viewingPatientDetails?.id === patient.id
                const isRelative = isRelativePatient(patient)
                const isPrimary = isPrimaryPatient(patient)
                // Check if there are both primary and relative patients in results
                const hasRelatives = searchResults.some(p => 
                  !p.isAddRelativeOption && isRelativePatient(p)
                )
                const hasPrimaries = searchResults.some(p => 
                  !p.isAddRelativeOption && isPrimaryPatient(p)
                )
                const hasBothTypes = hasRelatives && hasPrimaries
                
                return (
                  <Box
                    key={patient.id}
                    onClick={(e) => {
                      e.stopPropagation()
                      setViewingPatientDetails(patient)
                    }}
                    sx={{
                      padding: '16px',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      border: isSelected 
                        ? '2px solid #6779e3' 
                        : '1.5px solid rgba(226, 232, 240, 0.8)',
                      backgroundColor: isSelected ? 'rgba(103, 121, 227, 0.05)' : '#fff',
                      transition: 'all 0.2s ease',
                      boxShadow: isSelected 
                        ? '0 4px 12px rgba(103, 121, 227, 0.2)' 
                        : '0 1px 3px rgba(0, 0, 0, 0.05)',
                      '&:hover': {
                        borderColor: '#6779e3',
                        backgroundColor: 'rgba(103, 121, 227, 0.03)',
                        boxShadow: '0 4px 12px rgba(103, 121, 227, 0.15)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      {/* Avatar with Age */}
                      <Box
                        sx={{
                          width: 72,
                          height: 72,
                          borderRadius: '18px',
                          backgroundColor: getAvatarColor(patient.gender),
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          position: 'relative',
                          border: `3px solid ${getIconColor(patient.gender)}40`,
                          overflow: 'visible',
                          paddingBottom: getPatientAge(patient.dateOfBirth) ? '8px' : '0',
                        }}
                      >
                        {(() => {
                          const GenderIcon = getGenderIcon(patient.gender)
                          const iconStyle = getGenderIconStyle(patient.gender)
                          return (
                            <GenderIcon 
                              sx={{ 
                                fontSize: 40, 
                                color: getIconColor(patient.gender),
                                ...iconStyle,
                              }} 
                            />
                          )
                        })()}
                        {getPatientAge(patient.dateOfBirth) && (
                          <Typography
                            sx={{
                              position: 'absolute',
                              bottom: -4,
                              fontSize: '14px',
                              fontWeight: 900,
                              color: 'white',
                              backgroundColor: getIconColor(patient.gender),
                              borderRadius: '8px',
                              padding: '4px 8px',
                              lineHeight: 1.2,
                              boxShadow: '0 3px 8px rgba(0,0,0,0.3)',
                              border: '2.5px solid white',
                              minWidth: '36px',
                              textAlign: 'center',
                              letterSpacing: '0.5px',
                            }}
                          >
                            {getPatientAge(patient.dateOfBirth)}
                          </Typography>
                        )}
                      </Box>
                      
                      {/* Patient Info */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography
                            variant="body1"
                            sx={{
                              fontSize: '16px',
                              fontWeight: 700,
                              color: '#1e293b',
                            }}
                          >
                            {patient.name}
                          </Typography>
                          {isPrimary && hasBothTypes && (
                            <StarIcon sx={{ fontSize: 18, color: '#f6c0aa' }} />
                          )}
                          {isRelative && (
                            <Chip
                              label="Relative"
                              size="small"
                              sx={{
                                height: '20px',
                                fontSize: '10px',
                                fontWeight: 600,
                                backgroundColor: 'rgba(116, 80, 170, 0.1)',
                                color: '#7450aa',
                                border: '1px solid rgba(116, 80, 170, 0.2)',
                              }}
                            />
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {contactInfo.type === 'email' && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <EmailIcon sx={{ fontSize: 14, color: '#64748b' }} />
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: '13px',
                                  color: '#64748b',
                                }}
                              >
                                {contactInfo.formatted}
                              </Typography>
                            </Box>
                          )}
                          {contactInfo.type === 'mobile' && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PhoneIcon sx={{ fontSize: 14, color: '#64748b' }} />
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: '13px',
                                  color: '#64748b',
                                }}
                              >
                                {contactInfo.formatted}
                              </Typography>
                            </Box>
                          )}
                          {contactInfo.type === 'none' && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <ContactSupportIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: '12px',
                                  color: '#94a3b8',
                                  fontStyle: 'italic',
                                }}
                              >
                                No contact details available
                              </Typography>
                            </Box>
                          )}
                          {patient.patientNumber && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <BadgeIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: '12px',
                                  color: '#94a3b8',
                                }}
                              >
                                #{patient.patientNumber}
                              </Typography>
                            </Box>
                          )}
                          {(getPatientAge(patient.dateOfBirth) || patient.gender) && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                              {patient.gender && (
                                <Chip
                                  label={getGenderLabel(patient.gender)}
                                  size="small"
                                  sx={{
                                    height: '18px',
                                    fontSize: '10px',
                                    fontWeight: 500,
                                    backgroundColor: getAvatarColor(patient.gender),
                                    color: getIconColor(patient.gender),
                                    border: `1px solid ${getIconColor(patient.gender)}30`,
                                  }}
                                />
                              )}
                              {getPatientAge(patient.dateOfBirth) && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: '11px',
                                    color: '#64748b',
                                    fontWeight: 500,
                                  }}
                                >
                                  {getPatientAge(patient.dateOfBirth)} years
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      </Box>

                      {/* Action Buttons */}
                      <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                        {screenSize.width > 768 && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation()
                              setViewingPatientDetails(patient)
                            }}
                            sx={{
                              color: '#6779e3',
                              backgroundColor: 'rgba(103, 121, 227, 0.05)',
                              border: '1px solid rgba(103, 121, 227, 0.2)',
                              padding: '6px',
                              '&:hover': {
                                backgroundColor: 'rgba(103, 121, 227, 0.1)',
                                borderColor: 'rgba(103, 121, 227, 0.4)',
                              },
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <PersonIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation()
                            setSelectedPatient(patient)
                            setShowAddRelativeForm(true)
                          }}
                          sx={{
                            color: '#7450aa',
                            backgroundColor: 'rgba(116, 80, 170, 0.05)',
                            border: '1px solid rgba(116, 80, 170, 0.2)',
                            padding: '6px',
                            '&:hover': {
                              backgroundColor: 'rgba(116, 80, 170, 0.1)',
                              borderColor: 'rgba(116, 80, 170, 0.4)',
                            },
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <FamilyRestroomIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                )
              })}
            </Box>
          </>
        ) : hasSearched ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '60px 20px',
              color: '#64748b',
              textAlign: 'center',
            }}
          >
            <SearchIcon sx={{ fontSize: '64px', marginBottom: '16px', opacity: 0.3 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {searchQuery ? 'No patients found' : 'No patients available'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#94a3b8', mb: 3 }}>
              {searchQuery 
                ? `No results for "${searchQuery}". Try a different search term.` 
                : 'Start typing to search for patients.'}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<PersonAddIcon />}
              onClick={() => setActiveTab(1)}
              sx={{
                borderColor: 'rgba(103, 121, 227, 0.3)',
                color: '#6779e3',
                '&:hover': {
                  borderColor: 'rgba(103, 121, 227, 0.6)',
                  backgroundColor: 'rgba(103, 121, 227, 0.05)',
                },
              }}
            >
              Add New Patient
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '60px 20px',
              color: '#64748b',
            }}
          >
            <SearchIcon sx={{ fontSize: '64px', marginBottom: '16px', opacity: 0.3 }} />
            <Typography variant="body2" sx={{ textAlign: 'center' }}>
              Loading patients...
            </Typography>
          </Box>
        )}
          </Box>
        </Box>
        
        {/* Right Panel - Patient Details (Desktop only) */}
        {screenSize.width > 768 && viewingPatientDetails && (
          <Box sx={{ 
            width: '50%', 
            display: 'flex', 
            flexDirection: 'column',
            backgroundColor: '#fff',
            borderLeft: '1px solid rgba(226, 232, 240, 0.8)',
            overflowY: 'auto',
          }}>
            <Box sx={{ flex: 1, padding: '16px' }}>
              {renderPatientDetailsContent()}
            </Box>
          </Box>
        )}
        
        {/* Mobile Patient Details Overlay */}
        {screenSize.width <= 768 && viewingPatientDetails && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: '#fff',
              zIndex: 10,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Sticky Header with Back Button */}
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                backgroundColor: '#fff',
                zIndex: 1,
                borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              }}
            >
              <IconButton
                onClick={() => setViewingPatientDetails(null)}
                sx={{
                  color: '#6779e3',
                  backgroundColor: 'rgba(103, 121, 227, 0.1)',
                  padding: '8px',
                  '&:hover': {
                    backgroundColor: 'rgba(103, 121, 227, 0.15)',
                  },
                }}
              >
                <ArrowBackIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <Typography
                variant="h6"
                sx={{
                  fontSize: '18px',
                  fontWeight: 700,
                  color: '#1e293b',
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                Patient Details
              </Typography>
            </Box>
            
            <Box sx={{ flex: 1, padding: '16px', paddingBottom: '24px' }}>
              {renderPatientDetailsContent()}
            </Box>
          </Box>
        )}
        
        {/* Add Relative Modal */}
        {showAddRelativeForm && selectedPatient && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: '#fff',
              zIndex: 10,
              overflowY: 'auto',
            }}
          >
            <Box
              sx={{
                padding: '20px 24px',
                backgroundColor: 'rgba(103, 121, 227, 0.04)',
                borderBottom: '1px solid rgba(103, 121, 227, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                position: 'sticky',
                top: 0,
                zIndex: 1,
              }}
            >
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    fontSize: '18px',
                    color: '#6779e3',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <FamilyRestroomIcon />
                  Add Relative Patient
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.5 }}>
                  Primary Contact: {selectedPatient.name}
                </Typography>
              </Box>
              <IconButton
                onClick={() => {
                  setShowAddRelativeForm(false)
                  setSelectedPatient(null)
                }}
                sx={{
                  color: '#64748b',
                  '&:hover': {
                    backgroundColor: 'rgba(100, 116, 139, 0.1)',
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            
            <Box sx={{ padding: '20px' }}>
              <NewPatientForm
                clinicBrandId={clinicBrandId}
                clinicId={clinicId}
                mentorProfileData={mentorProfileData}
                createPatientApiSignature={createPatientApiSignature}
                primaryContactPatient={selectedPatient}
                afterAddPatientFunction={(patInfo) => {
                  handlePatientSelect(patInfo)
                }}
                closeDialog={() => {
                  setShowAddRelativeForm(false)
                  setSelectedPatient(null)
                }}
                isRelativePatient={true}
              />
            </Box>
          </Box>
        )}
      </Box>
    )
  }
  

  const renderAddNewTab = () => {
    // Determine what to prefill based on search query
    let prefillMobile = null
    let prefillEmail = null
    
    if (prefillSearchQuery) {
      if (validateEmail(prefillSearchQuery)) {
        prefillEmail = prefillSearchQuery
      } else {
        prefillMobile = prefillSearchQuery
      }
    }
    
    return (
      <Box sx={{ padding: '20px', height: '100%', overflowY: 'auto' }}>
        <NewPatientForm
          clinicBrandId={clinicBrandId}
          clinicId={clinicId}
          mentorProfileData={mentorProfileData}
          createPatientApiSignature={createPatientApiSignature}
          afterAddPatientFunction={handleNewPatientAdded}
          closeDialog={handleClose}
          initialMobile={prefillMobile}
          initialEmail={prefillEmail}
        />
      </Box>
    )
  }

  return (
    <>
    <Drawer
      anchor={screenSize.width > 768 ? 'right' : 'bottom'}
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: screenSize.width > 768 
            ? (viewingPatientDetails ? '1400px' : '900px')
            : '100vw',
          maxWidth: screenSize.width > 768 ? '95vw' : '100vw',
          height: screenSize.width > 768 ? '100vh' : '90vh',
          borderRadius: screenSize.width > 768 ? 0 : '16px 16px 0 0',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 0.3s ease',
        },
      }}
      ModalProps={{
        keepMounted: false,
        disableScrollLock: true, // We handle scroll lock with useBodyScrollLock hook
        disableEnforceFocus: false, // Allow focus management
        disableAutoFocus: false, // Allow auto-focus on open
        hideBackdrop: false,
      }}
    >
      <Box
        ref={drawerRef}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#f8fafc',
        }}
      >
        {/* Header - Compact with Tabs */}
        <Box
          sx={{
            backgroundColor: '#fff',
            flexShrink: 0,
            borderBottom: '2px solid rgba(226, 232, 240, 0.8)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2 }}>
            {/* Tabs */}
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                flex: 1,
                minHeight: '48px',
                '& .MuiTabs-indicator': {
                  backgroundColor: '#6779e3',
                  height: '3px',
                  borderRadius: '3px 3px 0 0',
                },
                '& .MuiTabs-flexContainer': {
                  gap: 0,
                },
              }}
            >
              <Tab
                icon={<SearchIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label="Search Patient"
                sx={{
                  fontWeight: 600,
                  fontSize: '14px',
                  textTransform: 'none',
                  color: activeTab === 0 ? '#6779e3' : '#64748b',
                  '&.Mui-selected': {
                    color: '#6779e3',
                  },
                  minHeight: '48px',
                  px: 2,
                }}
              />
              <Tab
                icon={<PersonAddIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label="Add New Patient"
                sx={{
                  fontWeight: 600,
                  fontSize: '14px',
                  textTransform: 'none',
                  color: activeTab === 1 ? '#6779e3' : '#64748b',
                  '&.Mui-selected': {
                    color: '#6779e3',
                  },
                  minHeight: '48px',
                  px: 2,
                }}
              />
            </Tabs>
            
            {/* Close Button */}
            <IconButton
              onClick={handleClose}
              sx={{
                color: '#64748b',
                ml: 1,
                '&:hover': {
                  backgroundColor: 'rgba(100, 116, 139, 0.1)',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Tab Content */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, position: 'relative' }}>
          {activeTab === 0 && renderSearchTab()}
          {activeTab === 1 && renderAddNewTab()}
        </Box>
      </Box>
      
    </Drawer>
    
    {/* Edit Patient Dialog */}
    <Dialog
      open={editPatientDialogOpen}
      onClose={() => {
        if (!isSavingPatient) {
          setEditPatientDialogOpen(false)
          setEditingPatientData(null)
        }
      }}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
        pb: 2,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <EditIcon sx={{ fontSize: 24, color: '#6779e3' }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
            Edit Patient Details
          </Typography>
        </Box>
        <IconButton
          onClick={() => {
            if (!isSavingPatient) {
              setEditPatientDialogOpen(false)
              setEditingPatientData(null)
            }
          }}
          disabled={isSavingPatient}
          sx={{
            color: '#64748b',
            '&:hover': {
              backgroundColor: 'rgba(100, 116, 139, 0.1)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {editingPatientData && (
          <PatientFormComponentV2
            editMode={true}
            patientDetails={editingPatientData}
            setPatientDetails={setEditingPatientData}
            clinicId={clinicId}
            clinicBrandId={clinicBrandId}
            disablePatientSearch={true}
            patientSearchApiSignature={searchPatientApiSignature}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ 
        padding: '16px 24px', 
        borderTop: '1px solid rgba(226, 232, 240, 0.8)',
        gap: 1,
      }}>
        <Button
          onClick={() => {
            if (!isSavingPatient) {
              setEditPatientDialogOpen(false)
              setEditingPatientData(null)
            }
          }}
          disabled={isSavingPatient}
          sx={{
            color: '#64748b',
            textTransform: 'none',
            fontWeight: 500,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSavePatientEdit}
          variant="contained"
          disabled={isSavingPatient || !editingPatientData}
          startIcon={isSavingPatient ? <CircularProgress size={16} sx={{ color: 'white' }} /> : null}
          sx={{
            backgroundColor: '#6779e3',
            color: 'white',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: '#7450aa',
            },
            '&.Mui-disabled': {
              backgroundColor: 'rgba(103, 121, 227, 0.3)',
            },
          }}
        >
          {isSavingPatient ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  )
}
